/**
 * Environment-mode helper — controls visibility of demo data on production.
 *
 * Why: parents/staff who land on a deployed customer instance must NEVER
 * see seeded demo credentials or "test mode" badges. Demo affordances are
 * only acceptable on preview / staging / local-dev hosts.
 *
 * Detection is FAIL-CLOSED: a host is treated as **production** unless it
 * explicitly looks like a known dev/preview environment. This means:
 *   • mangocubs.com               → live (whitelisted + fallback)
 *   • app.acme-preschool.com     → live (custom prod domain — unknown to us)
 *   • localhost / *.preview.*    → test (preview / dev only)
 *
 * Override hooks (rarely needed):
 *   • Set REACT_APP_ENV_MODE=live|test to force a value at build time.
 *   • Set REACT_APP_LIVE_HOSTS="a.com,b.com" to add explicit live domains.
 */
const FALLBACK_LIVE_HOSTS = [
  "mangocubs.com",
  "www.mangocubs.com",
  "app.mangocubs.com",
];

// Patterns that mean "this is NOT production" — preview/dev only.
const PREVIEW_HOST_PATTERNS = [
  /\.preview\./,           // *.preview.emergentagent.com (Emergent preview)
  /\.preview-/,            // some preview URL variants
  /^localhost$/,
  /^127\./,
  /^0\.0\.0\.0$/,
  /^192\.168\./,
  /^10\./,
  /\.local$/,
  /\.staging\./,
  /\.dev\./,
  /\.test\./,
  /^staging-/,
  /^dev-/,
  /^preview-/,
];

function liveHosts() {
  const fromEnv = (process.env.REACT_APP_LIVE_HOSTS || "")
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  return [...new Set([...FALLBACK_LIVE_HOSTS, ...fromEnv])];
}

export function getEnvMode() {
  // Explicit build-time override wins.
  const explicit = (process.env.REACT_APP_ENV_MODE || "").trim().toLowerCase();
  if (explicit === "live" || explicit === "production") return "live";
  if (explicit === "test" || explicit === "preview" || explicit === "staging") return "test";

  if (typeof window === "undefined") return "live"; // SSR — fail closed.
  const host = (window.location.hostname || "").toLowerCase();

  // Explicit live whitelist (custom production domains).
  if (liveHosts().includes(host)) return "live";

  // Anything matching a preview/dev pattern is treated as test.
  if (PREVIEW_HOST_PATTERNS.some((re) => re.test(host))) return "test";

  // Unknown host (custom customer domain) → treat as live (fail closed).
  // This is what hides demo credentials on any deployed customer instance
  // that we haven't manually added to FALLBACK_LIVE_HOSTS.
  return "live";
}

export function isLiveMode() {
  return getEnvMode() === "live";
}

export function isTestMode() {
  return getEnvMode() === "test";
}
